import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { BellOff, ChevronLeft } from 'lucide-react';
import { useRegion } from '@/hooks/useRegions';
import { useBulkSilenceRegion } from '@/hooks/useAlertOverrides';
import RegionIncidents from '@/components/RegionIncidents';
import RegionSecurityPolicySection from '@/components/RegionSecurityPolicySection';
import { RegionVpnSection } from '@/features/vpn';
import SilenceAlertsModal from '@/components/SilenceAlertsModal';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageContainer } from '@/components/ui/page-container';

export default function RegionDetails() {
  const { id } = useParams<{ id: string }>();
  const { data: region, isLoading } = useRegion(id);
  const [activeTab, setActiveTab] = useState<'overview' | 'incidents'>('incidents');
  const [showSilenceModal, setShowSilenceModal] = useState(false);
  const bulkSilenceMutation = useBulkSilenceRegion();

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-6 w-48 mb-3" />
        <Skeleton className="h-9 w-64 mb-6" />
        <Skeleton className="h-32 w-full" />
      </PageContainer>
    );
  }

  if (!region) {
    return (
      <PageContainer>
        <Alert>
          <AlertDescription>Region not found.</AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <nav
        className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground mb-3"
        aria-label="Breadcrumb"
      >
        <Link to="/admin/regions" className="hover:text-foreground">Regions</Link>
        <ChevronLeft className="w-3 h-3 rotate-180" />
        <span className="text-foreground truncate">{region.name}</span>
      </nav>
      <Link
        to="/admin/regions"
        className="md:hidden inline-flex items-center gap-1.5 text-sm text-muted-foreground mb-3 -ml-1"
      >
        <ChevronLeft className="w-4 h-4" />
        All regions
      </Link>

      <div className="flex items-start justify-between gap-3 mb-5">
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">{region.name}</h1>
        <Button
          variant="outline"
          onClick={() => { setShowSilenceModal(true); }}
          className="flex-shrink-0"
        >
          <BellOff className="w-4 h-4" />
          <span className="hidden md:inline">Silence alerts</span>
          <span className="md:hidden">Silence</span>
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v as typeof activeTab); }}>
        <TabsList className="mb-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="incidents">Incidents</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 md:space-y-5">
          <section className="bg-card border border-border rounded-lg p-5">
            <dl className="text-sm grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2">
              <dt className="text-muted-foreground font-medium">Name</dt>
              <dd>{region.name}</dd>
              {region.createdAt ? (
                <>
                  <dt className="text-muted-foreground font-medium">Created</dt>
                  <dd>{new Date(region.createdAt).toLocaleString()}</dd>
                </>
              ) : null}
              {region.updatedAt ? (
                <>
                  <dt className="text-muted-foreground font-medium">Updated</dt>
                  <dd>{new Date(region.updatedAt).toLocaleString()}</dd>
                </>
              ) : null}
            </dl>
          </section>
          {id ? <RegionSecurityPolicySection regionId={id} /> : null}
          {id ? <RegionVpnSection regionId={id} /> : null}
        </TabsContent>

        <TabsContent value="incidents">
          {id ? <RegionIncidents regionId={id} /> : null}
        </TabsContent>
      </Tabs>

      {id ? (
        <SilenceAlertsModal
          isActive={showSilenceModal}
          onClose={() => { setShowSilenceModal(false); }}
          onSilence={(silenceUntil, reason) => {
            bulkSilenceMutation.mutate(
              { regionId: id, data: { silenceUntil, silenceReason: reason || undefined } },
              { onSuccess: () => { setShowSilenceModal(false); } },
            );
          }}
          isPending={bulkSilenceMutation.isPending}
        />
      ) : null}
    </PageContainer>
  );
}
